import { isMobile } from '@/components/utils/device'
import usePersistedState from '@/hooks/usePersistedState'
import React, { createContext, useCallback } from 'react'

/* eslint-disable react-refresh/only-export-components -- context module */

/** Keyword status column ids for 2HQ pipeline board; true = column body collapsed */
export type PipelineColumnCollapsedMap = Record<string, boolean>

type LayoutPreferenceContextType = {
    toolbarMinimized: boolean
    setToolbarMinimized: (minimized: boolean) => void
    contentView: 'list' | 'grid'
    setContentView: (view: 'list' | 'grid') => void
    isCollapsed: boolean
    setIsCollapsed: (collapsed: boolean) => void
    pipelineColumnCollapsed: PipelineColumnCollapsedMap
    togglePipelineColumnCollapsed: (columnId: string) => void
    sortCriteria: 'createdAt' | 'name' | 'last_opened' | 'updatedAt'
    setSortCriteria: (criteria: 'createdAt' | 'name' | 'last_opened' | 'updatedAt') => void
    sortOrder: 'asc' | 'desc'
    setSortOrder: (order: 'asc' | 'desc') => void
}

const defaultContext: LayoutPreferenceContextType = {
    toolbarMinimized: true,
    setToolbarMinimized: () => {},
    contentView: isMobile() ? 'grid' : 'list',
    setContentView: () => {},
    isCollapsed: false,
    setIsCollapsed: () => {},
    pipelineColumnCollapsed: {},
    togglePipelineColumnCollapsed: () => {},
    sortCriteria: 'updatedAt',
    setSortCriteria: () => {},
    sortOrder: 'desc',
    setSortOrder: () => {},
}

export const LayoutPreferenceContext = createContext<LayoutPreferenceContextType>(defaultContext)

export const LayoutPreferenceProvider = ({ children }: { children: React.ReactNode }) => {
    const [toolbarMinimized, setToolbarMinimized] = usePersistedState({
        key: 'toolbarMinimized',
        defaultValue: true,
    })
    const [contentView, setContentView] = usePersistedState({
        key: 'contentView',
        defaultValue: 'list',
    })

    const [isCollapsed, setIsCollapsed] = usePersistedState({
        key: 'isCollapsed',
        defaultValue: false,
    })

    const [pipelineColumnCollapsed, setPipelineColumnCollapsed] = usePersistedState({
        key: '2hq-pipeline-column-collapsed',
        defaultValue: {} as PipelineColumnCollapsedMap,
    })

    const togglePipelineColumnCollapsed = useCallback(
        (columnId: string) => {
            setPipelineColumnCollapsed((prev: unknown) => {
                const map =
                    prev && typeof prev === 'object' && !Array.isArray(prev)
                        ? (prev as PipelineColumnCollapsedMap)
                        : {}
                return { ...map, [columnId]: !map[columnId] }
            })
        },
        [setPipelineColumnCollapsed]
    )

    const [sortCriteria, setSortCriteria] = usePersistedState({
        key: 'sortCriteria',
        defaultValue: 'updatedAt',
    })
    const [sortOrder, setSortOrder] = usePersistedState({
        key: 'sortOrder',
        defaultValue: 'desc',
    })

    const value = {
        toolbarMinimized,
        setToolbarMinimized,
        contentView,
        setContentView,
        isCollapsed,
        setIsCollapsed,
        pipelineColumnCollapsed,
        togglePipelineColumnCollapsed,
        sortCriteria,
        setSortCriteria,
        sortOrder,
        setSortOrder,
    }

    return <LayoutPreferenceContext.Provider value={value}>{children}</LayoutPreferenceContext.Provider>
}
